import {
  Dialog, DialogActions, DialogContent, Grid, Typography, useMediaQuery, useTheme
} from '@mui/material';
import { ContainedButton } from 'components/ContainedButton';
import { OutlinedButton } from 'components/OutlinedButton';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { useStyles } from './styles';

type PromptChangeConfirmModalProps = {
  isOpen: boolean,
  onConfirm: () => void,
  onClose: () => void,
  description?: string,
  promptName?: string,
  showHistoryWarning?: boolean,
}

export const PromptChangeConfirmModal: FC<PromptChangeConfirmModalProps> = ({
  isOpen,
  onConfirm,
  onClose,
  description,
  promptName,
  showHistoryWarning = true,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const trimmedDescription = description?.trim() ?? '';
  const trimmedName = promptName?.trim() ?? '';
  const title = trimmedName !== ''
    ? t('questionsAndAnswers:promptChangeTitleWithName', { name: trimmedName })
    : t('questionsAndAnswers:promptChangeTitle');
  return (
    <Dialog
      open={isOpen}
      fullWidth
      fullScreen={isMobile}
      onClose={onClose}
      aria-labelledby="prompt-change-confirm-title"
      data-screen-exclude
    >
      <DialogContent style={{ padding: 0 }}>
        <Grid container direction="column">
          <Grid item>
            <Typography id="prompt-change-confirm-title" variant="h5" className={classes.title}>
              {title}
            </Typography>
          </Grid>
          <Grid item container direction="column" className={classes.container}>
            {trimmedDescription !== '' && (
              <Grid item style={{ marginBottom: showHistoryWarning ? theme.spacing(2) : 0 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  {t('questionsAndAnswers:promptChangeDescriptionLabel')}
                </Typography>
                <Typography variant="body1" style={{ whiteSpace: 'pre-line' }}>
                  {trimmedDescription}
                </Typography>
              </Grid>
            )}
            {trimmedDescription === '' && trimmedName !== '' && (
              <Grid item style={{ marginBottom: showHistoryWarning ? theme.spacing(2) : 0 }}>
                <Typography variant="body1">
                  {t('questionsAndAnswers:promptDescriptionUnavailable')}
                </Typography>
              </Grid>
            )}
            {showHistoryWarning && (
              <Grid item>
                <Typography variant="body1" style={{ whiteSpace: 'pre-line' }}>
                  {t('questionsAndAnswers:promptChangeMessage')}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions className={classes.actionButtons}>
        <OutlinedButton
          text={t('common:cancel')}
          icon="times"
          buttonColor="inherit"
          onClick={onClose}
        />
        <ContainedButton
          text={t('questionsAndAnswers:promptChangeConfirm')}
          icon="check"
          onClick={onConfirm}
        />
      </DialogActions>
    </Dialog>
  );
};
